import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '../components/common';
import {
  DashboardFilterBar,
  DashboardHeroCards,
  DashboardGrowthCards,
  DashboardVolumeChart,
  DashboardHealthCards,
  DashboardTopMerchants,
  DashboardActionWorklist,
  DashboardQuickActions,
  DashboardActivityFeed,
} from '../components/dashboard';
import dashboardService from '../services/dashboardService';
import { isDashboardAllowed } from '../utils';
import UnderConstructionPage from './UnderConstructionPage';

const getDateRangeForPreset = preset => {
  const now = new Date();
  const end = now.toISOString().split('T')[0];

  switch (preset) {
    case 'today':
      return { start: end, end };
    case '7d': {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      return { start: d.toISOString().split('T')[0], end };
    }
    case '30d': {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return { start: d.toISOString().split('T')[0], end };
    }
    case 'month': {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: startOfMonth.toISOString().split('T')[0], end };
    }
    case 'all':
      return { start: null, end: null };
    default:
      return { start: null, end: null };
  }
};

const DashboardPage = () => {
  const isAllowed = isDashboardAllowed();
  const [activePreset, setActivePreset] = useState('30d');
  const [startDate, setStartDate] = useState(() => getDateRangeForPreset('30d').start);
  const [endDate, setEndDate] = useState(() => getDateRangeForPreset('30d').end);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    metrics: {},
    volumeTrend: [],
    pendingQueue: { snaps: [], reversals: [] },
    topMerchants: [],
    recentActivities: [],
  });

  const fetchDashboard = useCallback(async (start, end) => {
    try {
      setLoading(true);
      setError(null);
      const res = await dashboardService.getDashboardData({
        startDate: start,
        endDate: end,
      });
      setData(res);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err.message || 'Unable to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load & when date range changes
  useEffect(() => {
    if (!isAllowed) return;
    fetchDashboard(startDate, endDate);
  }, [fetchDashboard, startDate, endDate, isAllowed]);

  const handlePresetChange = preset => {
    setActivePreset(preset);
    const range = getDateRangeForPreset(preset);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const handleDateRangeChange = ({ startDate: newStart, endDate: newEnd }) => {
    setActivePreset('custom');
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleRefresh = () => {
    if (isAllowed) {
      fetchDashboard(startDate, endDate);
    }
  };

  if (!isAllowed) {
    return (
      <PageHeader
        title='Dashboard'
        subtitle='Welcome to your SKAI1 Admin Panel'
        breadcrumbs={[{ name: 'Home', href: '/' }, { name: 'Dashboard' }]}
      />
    )
    // return <UnderConstructionPage />;
  }

  return (
    <div className='pb-8'>
      <PageHeader
        title='Executive Operations Command Center'
        subtitle='Real-time operational metrics, platform liquidity, verification queue, and system activity'
        breadcrumbs={[{ name: 'Home', href: '/' }, { name: 'Dashboard' }]}
      />

      {/* Filter Bar */}
      <DashboardFilterBar
        activePreset={activePreset}
        onPresetChange={handlePresetChange}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={handleRefresh}
        loading={loading}
      />

      {/* Error Alert */}
      {error && (
        <div className='bg-rose-50 border border-rose-200 rounded-xl p-4 mb-6 flex items-center justify-between text-xs text-rose-800'>
          <div className='flex items-center gap-2'>
            <svg className='w-5 h-5 text-rose-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z' />
            </svg>
            <span>{error}</span>
          </div>
          <button
            onClick={handleRefresh}
            className='font-bold underline hover:text-rose-900 cursor-pointer'
          >
            Retry
          </button>
        </div>
      )}

      {/* Skeleton Loader during initial load */}
      {loading && !data.metrics.grossVolume && (
        <div className='space-y-6 animate-pulse'>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5'>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className='bg-white p-5 rounded-xl border border-gray-200 h-28'></div>
            ))}
          </div>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
            {[1, 2, 3].map(i => (
              <div key={i} className='bg-white p-5 rounded-xl border border-gray-200 h-28'></div>
            ))}
          </div>
          <div className='grid grid-cols-1 lg:grid-cols-12 gap-6'>
            <div className='lg:col-span-8 space-y-6'>
              <div className='bg-white rounded-xl border border-gray-200 h-72'></div>
              <div className='bg-white rounded-xl border border-gray-200 h-40'></div>
            </div>
            <div className='lg:col-span-4 space-y-6'>
              <div className='bg-white rounded-xl border border-gray-200 h-64'></div>
              <div className='bg-white rounded-xl border border-gray-200 h-48'></div>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Content */}
      {(!loading || data.metrics.grossVolume !== undefined) && (
        <>
          {/* 4 Hero KPI Cards */}
          <DashboardHeroCards
            metrics={data.metrics}
            activePreset={activePreset}
          />

          {/* 3-Pillar Ecosystem Growth Hub */}
          <DashboardGrowthCards
            metrics={data.metrics}
          />

          {/* Asymmetric 70/30 Split Layout */}
          <div className='grid grid-cols-1 lg:grid-cols-12 gap-6 items-start'>
            {/* Left Column (65% width) - Deep Visual Analytics & Financial Trends */}
            <div className='lg:col-span-8 space-y-6'>
              {/* Daily Volume & Cashback SVG Chart */}
              <DashboardVolumeChart trendData={data.volumeTrend} />

              {/* Verification Throughput & Merchant Funding Health */}
              <DashboardHealthCards metrics={data.metrics} />

              {/* Top 5 Performing Merchants */}
              <DashboardTopMerchants topMerchants={data.topMerchants} />
            </div>

            {/* Right Column (35% width) - Operational Worklist, Quick Actions & Real-Time Feed */}
            <div className='lg:col-span-4 space-y-6'>
              {/* Urgent Action Worklist */}
              <DashboardActionWorklist pendingQueue={data.pendingQueue} />

              {/* Quick Actions Toolbar */}
              <DashboardQuickActions />

              {/* Real-Time Platform Activity Feed */}
              <DashboardActivityFeed activities={data.recentActivities} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
